import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { Shell } from "@/components/Shell";
import { Button, Card } from "@/components/ui-kit";

export const Route = createFileRoute("/")({
  component: Landing,
});

type FaqItemData = { q: string; a: string; bullets?: string[] };

const FAQ_SECTIONS: { title: string; items: FaqItemData[] }[] = [
  {
    title: "General",
    items: [
      {
        q: "What is CrediKing?",
        a: "CrediKing is a lending platform that provides reliable short-term loans to salary earners across Lagos.",
      },
      {
        q: "Who can apply for a loan?",
        a: "Any eligible salary earner who meets our verification and lending requirements.",
      },
      {
        q: "Is CrediKing licensed?",
        a: "Yes. CrediKing holds a money lenders licence from the Ministry of Home Affairs.",
      },
      {
        q: "Where do you operate?",
        a: "We currently serve customers across Lagos through our digital platform.",
      },
      {
        q: "How do I create an account?",
        a: "Register on our website, complete your profile, and complete the verification process.",
      },
      {
        q: "Is registration free?",
        a: "Yes. Creating an account is completely free.",
      },
      {
        q: "Can I get approved even if I don’t need a loan now?",
        a: "Yes. Getting approved early makes future loan applications faster and more convenient.",
      },
    ],
  },
  {
    title: "Eligibility",
    items: [
      {
        q: "What are CrediKing eligibility levels?",
        a: "CrediKing offers three eligibility levels — Express, Verified, and Elite — based on your documents and repayment history. Each level unlocks different loan limits and terms.",
      },
      {
        q: "What is Express?",
        a: "Express is designed for simple onboarding and quick access to credit.",
        bullets: [
          "Simple onboarding",
          "Bank statement required",
          "No guarantor or salary-job proof required",
          "Ideal for non-salary earners",
          "Weekly pricing based on how long you borrow (up to 30 days)",
          "Loan access up to ₦100,000",
        ],
      },
      {
        q: "What is Verified?",
        a: "Verified customers complete additional checks, including salary-job proof, guarantor, and next-of-kin confirmation.",
        bullets: [
          "Salary job evidence required",
          "Guarantor and next of kin on file",
          "Negotiate your own interest rate",
          "Higher borrowing limits",
          "Loan access up to ₦500,000",
        ],
      },
      {
        q: "What is Elite?",
        a: "Elite status is earned through consistent, responsible borrowing and timely repayments.",
        bullets: [
          "Loan access up to ₦10,000,000",
          "Highest borrowing limits",
          "Maximum flexibility on interest rates",
          "Customers are most likely to receive the interest rates they request, subject to approval",
        ],
      },
      {
        q: "Can I move from Express to Verified?",
        a: "Yes. Eligible customers can upgrade by completing the additional verification requirements.",
      },
      {
        q: "How do I become Elite?",
        a: "Elite cannot be purchased. It is earned through a strong repayment history, responsible borrowing behaviour, and maintaining a good standing with CrediKing.",
      },
      {
        q: "Is Elite guaranteed after a certain number of loans?",
        a: "No. Elite status is awarded based on your overall relationship with CrediKing, including repayment consistency, credit behaviour, and internal risk assessment.",
      },
      {
        q: "Which eligibility level is best?",
        a: "Each level is designed for different needs:",
        bullets: [
          "Express is ideal for non-salary earners who want simple onboarding.",
          "Verified offers higher limits and the ability to negotiate your own interest rate.",
          "Elite provides the highest borrowing limits and the greatest flexibility on pricing.",
        ],
      },
    ],
  },
  {
    title: "Loan Eligibility",
    items: [
      {
        q: "Who qualifies for a loan?",
        a: "Eligibility depends on several factors, including:",
        bullets: [
          "Identity verification",
          "Employment verification",
          "Credit assessment",
          "Internal risk assessment",
        ],
      },
      {
        q: "Is being employed compulsory?",
        a: "Express does not require proof of a salary job, but a bank statement is required. Verified (and Elite) require salary-job evidence, next of kin, and a guarantor.",
      },
      {
        q: "Is there an age requirement?",
        a: "Applicants must be at least 18 years old.",
      },
      {
        q: "Do I need a guarantor?",
        a: "Express does not require a guarantor. Verified and Elite do — we may contact your guarantor.",
      },
    ],
  },
  {
    title: "Loan Amount",
    items: [
      {
        q: "How much can I borrow?",
        a: "Loans range from ₦1,000 to ₦10,000,000, depending on your eligibility level.",
      },
      {
        q: "Why wasn’t I offered the amount I requested?",
        a: "Loan limits are determined through our affordability assessment, credit evaluation, and internal lending policies.",
      },
      {
        q: "Will my loan limit increase?",
        a: "Yes. Consistent, responsible borrowing and timely repayments may increase your loan limit over time.",
      },
    ],
  },
  {
    title: "Loan Application",
    items: [
      {
        q: "How long does it take to apply?",
        a: "Most applications take only a few minutes to complete.",
      },
      {
        q: "What documents do I need?",
        a: "You may be asked to provide:",
        bullets: [
          "A valid government-issued ID (NIN)",
          "Bank Verification Number (BVN)",
          "Selfie verification",
          "Bank statement (required for Express and above)",
          "Proof of salary (Verified and Elite)",
          "Next of kin and guarantor (Verified and Elite)",
          "Bank account details",
        ],
      },
      {
        q: "Will checking my eligibility affect my credit score?",
        a: "Eligibility checks alone generally do not affect your credit profile.",
      },
      {
        q: "Can I edit my application after submitting it?",
        a: "Yes, but only before it enters processing.",
      },
    ],
  },
  {
    title: "Loan Approval",
    items: [
      {
        q: "How long does approval take?",
        a: "Approval times vary depending on the verification requirements and assessment process. We review as soon as possible.",
      },
      {
        q: "Is loan approval guaranteed?",
        a: "No. Every application is assessed individually before a lending decision is made.",
      },
      {
        q: "Why was my application declined?",
        a: "Your application may be declined due to:",
        bullets: [
          "Incomplete information",
          "Failed verification",
          "Poor affordability assessment",
          "Existing repayment concerns",
          "Internal lending policies",
        ],
      },
      {
        q: "Can I apply again after being declined?",
        a: "Yes. You may reapply after resolving the issue that affected your application.",
      },
    ],
  },
  {
    title: "Loan Disbursement",
    items: [
      {
        q: "How will I receive my money?",
        a: "Approved loans are paid directly into your verified bank account.",
      },
      {
        q: "Can someone else’s account receive my loan?",
        a: "No. Loans can only be disbursed to your verified bank account.",
      },
      {
        q: "What if I entered the wrong bank account?",
        a: "Please contact Customer Support immediately.",
      },
    ],
  },
  {
    title: "Repayment",
    items: [
      {
        q: "When do I repay my loan?",
        a: "You can choose your preferred repayment date, provided it falls within your loan term of up to 30 days.",
      },
      {
        q: "Can I repay early?",
        a: "Yes. Early repayment is allowed.",
      },
      {
        q: "Is there a penalty for early repayment?",
        a: "No.",
      },
      {
        q: "Can I pay in instalments?",
        a: "Depending on the loan product, instalment repayment options may be available.",
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept:",
        bullets: ["Bank Transfer", "Debit Card", "Other supported payment channels"],
      },
      {
        q: "What happens if I miss my repayment date?",
        a: "Our Support Team will contact you to discuss repayment. Grace periods depend on your eligibility level: Express 1 day, Verified 3 days, Elite 5 days. Late repayments may also affect your future borrowing eligibility and your credit history.",
      },
    ],
  },
  {
    title: "Loan Extension",
    items: [
      {
        q: "Can I extend my loan?",
        a: "Eligible customers may request a loan rollover (carryover), depending on their eligibility level. Express loans cannot be rolled over.",
      },
      {
        q: "How many times can I roll over my loan?",
        a: "Verified loans may be rolled over up to two times. Elite rollovers are unlimited but subject to CrediKing’s approval and applicable terms. Express does not allow rollover.",
      },
      {
        q: "Will I need to submit a new application?",
        a: "No. A rollover is different from applying for a new loan.",
      },
    ],
  },
  {
    title: "Fees & Interest",
    items: [
      {
        q: "How is interest calculated?",
        a: "Interest depends on the loan amount, repayment period, and applicable pricing. Fully verified customers may qualify for preferential pricing based on our internal assessment.",
      },
      {
        q: "Can verified customers negotiate their interest rate?",
        a: "Yes. Verified and Elite customers may request a personalized interest rate, subject to approval based on risk assessment, loan terms, and internal lending policies.",
      },
      {
        q: "Are there hidden charges?",
        a: "No. All applicable fees and charges are disclosed before you accept your loan.",
      },
      {
        q: "Will I know the total amount I have to repay?",
        a: "Yes. Your total repayment amount will always be displayed before you accept your loan.",
      },
    ],
  },
  {
    title: "Verification",
    items: [
      {
        q: "Why do you require my BVN?",
        a: "Your BVN helps us verify your identity and protect against fraud.",
      },
      {
        q: "Is my information secure?",
        a: "Yes. We use industry-standard security measures to protect your personal information.",
      },
      {
        q: "Why do you request a selfie?",
        a: "To confirm that the applicant matches the submitted identity documents.",
      },
    ],
  },
  {
    title: "Credit & Borrowing",
    items: [
      {
        q: "Will taking a loan improve my credit profile?",
        a: "Responsible borrowing and timely repayments may contribute positively to your credit history.",
      },
      {
        q: "Can I have multiple loans at the same time?",
        a: "Eligibility depends on your current obligations and our lending policy.",
      },
      {
        q: "Can I apply while I still have an existing loan?",
        a: "Generally, existing loans should be repaid before another loan is approved. However, eligible fully verified customers may qualify for a loan top-up, subject to approval.",
      },
    ],
  },
  {
    title: "Customer Support",
    items: [
      {
        q: "How can I contact Customer Support?",
        a: "You can reach us through:",
        bullets: ["Phone", "Email", "WhatsApp", "In-app Support"],
      },
      {
        q: "What are your customer service hours?",
        a: "Our support hours are available on our website and mobile app.",
      },
    ],
  },
  {
    title: "Security",
    items: [
      {
        q: "Will CrediKing ever ask for my password?",
        a: "No. Never share your password or One-Time Password (OTP) with anyone.",
      },
      {
        q: "How do I know messages are genuinely from CrediKing?",
        a: "Always verify communications through our official channels.",
      },
      {
        q: "What should I do if I suspect fraud?",
        a: "Contact Customer Support immediately.",
      },
    ],
  },
  {
    title: "Technical Support",
    items: [
      {
        q: "I forgot my password.",
        a: "On the sign-in page, tap Forgot password, enter your email, and follow the reset link we send you.",
      },
      {
        q: "I didn’t receive my OTP.",
        a: "Please wait a few minutes before requesting another OTP.",
      },
      {
        q: "My app keeps crashing.",
        a: "Update the app to the latest version. If the issue persists, contact Customer Support.",
      },
      {
        q: "My account is locked.",
        a: "Please contact Customer Support for assistance.",
      },
    ],
  },
  {
    title: "Banking",
    items: [
      {
        q: "Which banks do you support?",
        a: "Most major Nigerian banks are supported.",
      },
      {
        q: "Can I change my bank account?",
        a: "Yes. Changes are subject to verification.",
      },
    ],
  },
  {
    title: "Business Loans",
    items: [
      {
        q: "Do you offer business loans?",
        a: "Yes. Eligible businesses may qualify for selected business loan products.",
      },
      {
        q: "Can registered companies apply?",
        a: "Yes. Additional documentation may be required.",
      },
    ],
  },
  {
    title: "Responsible Borrowing",
    items: [
      {
        q: "Should I borrow if I’m unsure I can repay?",
        a: "No. Borrow only what you can comfortably repay.",
      },
      {
        q: "What happens if I default?",
        a: "Defaulting may result in recovery actions, additional charges where applicable, reduced access to future credit, and other actions permitted under your loan agreement and applicable laws.",
      },
    ],
  },
  {
    title: "Miscellaneous",
    items: [
      {
        q: "Can I cancel my application?",
        a: "Yes, provided processing has not been completed.",
      },
      {
        q: "Can I delete my account?",
        a: "Yes. Please contact Customer Support for assistance.",
      },
      {
        q: "Can I refer friends?",
        a: "Yes. Verified customers can earn referral rewards. Referred customers may also request you to serve as their guarantor, where applicable.",
      },
      {
        q: "Do you offer promotions?",
        a: "Occasionally. Follow our official channels for the latest offers and promotions.",
      },
      {
        q: "Why should I choose CrediKing?",
        a: "Because we provide reliable, transparent, and customer-focused lending with straightforward terms, responsive support, and a commitment to responsible lending.",
      },
    ],
  },
];

const TRUST_ITEMS = [
  "★ Borrow on your terms",
  "Express · Verified · Elite",
  "1–30 day terms",
  "Licensed money lender",
];

const TIERS = [
  {
    name: "Express",
    slug: "express" as const,
    tagline: "Simple application. No guarantor required.",
    limit: "Up to ₦100,000",
    audience: "For non-salary earners.",
  },
  {
    name: "Verified",
    slug: "verified" as const,
    tagline: "Higher access. Negotiate your interest rate.",
    limit: "Up to ₦500,000",
    audience: "For verified salary earners.",
  },
  {
    name: "Elite",
    slug: "elite" as const,
    tagline: "Earned status. Greater flexibility when negotiating your interest rate.",
    limit: "Up to ₦10,000,000",
    audience: "Granted based on your CrediKing history and profile.",
  },
];

const btnLg =
  "px-7 py-3.5 text-base sm:px-8 sm:py-4 sm:text-lg rounded-lg";

function Landing() {
  return (
    <Shell>
      <section
        id="top"
        className="hero-stage relative overflow-hidden -mt-px min-h-0 md:min-h-[min(88vh,820px)] flex items-center scroll-mt-20"
      >
        <div className="hero-stage__media absolute inset-0" aria-hidden="true">
          <img
            src="/hero-lending.jpg"
            alt=""
            className="hero-stage__img"
            width={1920}
            height={1080}
            decoding="async"
            fetchPriority="high"
          />
        </div>
        <div className="hero-stage__veil absolute inset-0" aria-hidden="true" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 py-14 sm:py-20 md:py-32 w-full">
          <div className="max-w-xl">
            <p className="font-display text-5xl sm:text-5xl md:text-6xl tracking-tight text-[var(--navy-ink)]">
              Credi<span className="text-gold">King</span>
            </p>
            <h1 className="mt-3 font-display text-4xl sm:text-4xl md:text-5xl leading-[1.1] tracking-tight text-[var(--navy-ink)]">
              Borrow, <span className="text-gold">on your terms.</span>
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-[var(--navy-muted)] max-w-md">
              Need short term loans with reasonable interest rates?
            </p>
            <div className="mt-7 md:mt-9 flex flex-wrap gap-3 sm:gap-4">
              <Button variant="gold" asChild className={btnLg}>
                <Link to="/signup">Tap in to get started</Link>
              </Button>
              <Button variant="outline" asChild className={`${btnLg} border-[var(--navy-ink)]/25 text-[var(--navy-ink)] hover:border-gold hover:text-gold bg-white/40 backdrop-blur-sm`}>
                <Link to="/login">I have an account</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="trust-marquee border-y border-border/60 bg-accent/20" aria-label="Trust signals">
        <div className="trust-marquee__track">
          {[0, 1].map((copy) =>
            TRUST_ITEMS.map((item, i) => (
              <Fragment key={`${copy}-${i}`}>
                <span className="trust-marquee__item">{item}</span>
                <span className="trust-marquee__dot" aria-hidden="true">
                  ·
                </span>
              </Fragment>
            )),
          )}
        </div>
      </section>

      <section id="how-it-works" className="scroll-mt-20">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-gold">How it works</div>
            <h2 className="font-display text-4xl mt-2">Become eligible now, borrow when you need it</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                t: "Sign Up",
                d: "Create your account with your name, email, phone number and password. Complete your KYC to become eligible, so you can borrow when you need to.",
              },
              {
                t: "Become eligible",
                d: "Complete your KYC and choose Express or Verified based on your needs. Build a strong repayment history to earn Elite and unlock higher limits.",
              },
              {
                t: "Apply & Repay",
                d: "Choose a repayment period of up to 30 days, review your total repayment, and accept only when you’re comfortable. Every request is carefully reviewed.",
              },
            ].map((s, i) => (
              <Card key={s.t} className="relative">
                <div className="absolute -top-3 -left-3 h-10 w-10 rounded-full bg-gradient-gold grid place-items-center font-display text-navy-deep">{i + 1}</div>
                <div className="font-display text-xl pt-2">{s.t}</div>
                <p className="mt-3 text-sm text-muted-foreground">{s.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="eligibility" className="border-t border-border/60 bg-accent/10 scroll-mt-20">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-gold">Eligibility</div>
            <h2 className="font-display text-4xl mt-2">Express. Verified. Elite.</h2>
            <p className="mt-3 text-muted-foreground">
              Clear eligibility levels for different needs — from simple application to our highest limits.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.map((tier) => (
              <div
                key={tier.name}
                className="rounded-xl border border-border/70 bg-card/50 p-6 flex flex-col"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-gold">{tier.name}</div>
                <p className="mt-3 font-display text-2xl leading-snug">{tier.tagline}</p>
                <p className="mt-3 text-sm text-gold font-medium">{tier.limit}</p>
                <p className="mt-2 text-sm text-muted-foreground">{tier.audience}</p>
                <div className="mt-6">
                  <Button variant="outline" asChild className="w-full sm:w-auto">
                    {tier.slug === "express" ? (
                      <Link to="/eligibility/express">Learn more</Link>
                    ) : tier.slug === "verified" ? (
                      <Link to="/eligibility/verified">Learn more</Link>
                    ) : (
                      <Link to="/eligibility/elite">Learn more</Link>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button variant="gold" asChild className={btnLg}>
              <Link to="/signup">Tap in to get started</Link>
            </Button>
          </div>
        </div>
      </section>

      <section id="faq" className="border-t border-border/60 bg-accent/10 scroll-mt-20">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-gold">FAQ</div>
            <h2 className="font-display text-4xl mt-2">Frequently asked questions</h2>
          </div>
          <FaqTabs sections={FAQ_SECTIONS} />
        </div>
      </section>

      <section id="get-started" className="border-t border-border/60 scroll-mt-20">
        <div className="mx-auto max-w-7xl px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-3xl">Ready when you are.</h3>
            <p className="text-muted-foreground mt-2">
              Need short term loans with reasonable interest rates? Tap in.
            </p>
          </div>
          <Button variant="gold" asChild className={btnLg}>
            <Link to="/signup">Tap in to get started</Link>
          </Button>
        </div>
      </section>
    </Shell>
  );
}

function FaqTabs({ sections }: { sections: { title: string; items: FaqItemData[] }[] }) {
  const [active, setActive] = useState(sections[0]?.title ?? "");
  const current = sections.find((s) => s.title === active) ?? sections[0];

  return (
    <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
      <nav
        role="tablist"
        aria-label="FAQ categories"
        className="w-full md:w-56 shrink-0 flex flex-col gap-1 max-h-52 md:max-h-[min(70vh,36rem)] overflow-y-auto md:sticky md:top-24 pr-1"
      >
        {sections.map((section) => {
          const selected = section.title === active;
          return (
            <button
              key={section.title}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActive(section.title)}
              className={`w-full text-left rounded-md px-3.5 py-2.5 text-sm transition border ${
                selected
                  ? "bg-gradient-gold text-navy-deep border-transparent font-medium shadow-gold"
                  : "border-border/60 bg-card/40 text-muted-foreground hover:border-gold/40 hover:text-foreground"
              }`}
            >
              {section.title}
            </button>
          );
        })}
      </nav>
      <div role="tabpanel" className="min-w-0 flex-1 space-y-2 w-full">
        <h3 className="font-display text-xl text-gold mb-3 md:mb-4">{current?.title}</h3>
        {current?.items.map((f) => (
          <FaqItem key={`${current.title}-${f.q}`} q={f.q} a={f.a} bullets={f.bullets} />
        ))}
      </div>
    </div>
  );
}

function FaqItem({ q, a, bullets }: FaqItemData) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      className="w-full text-left rounded-xl border border-border bg-card/40 px-5 py-4 hover:border-gold/40 transition"
    >
      <div className="flex items-start justify-between gap-4">
        <span className="font-medium">{q}</span>
        <span className="text-gold shrink-0">{open ? "−" : "+"}</span>
      </div>
      {open && (
        <div className="mt-3 text-sm text-muted-foreground space-y-2">
          <p>{a}</p>
          {bullets && bullets.length > 0 && (
            <ul className="list-disc pl-5 space-y-1">
              {bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </button>
  );
}
